import React, { useCallback, useEffect, useState } from "react";

import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { ChronoUnitStr } from "api/model/enums";
import { ScatterData } from "plotly.js";
import { Button } from "primereact/button";
import { ColorPickerValueType } from "primereact/colorpicker";
import { Dialog } from "primereact/dialog";
import { useResizeDetector } from "react-resize-detector";
import { addToDate } from "../../../utils/time-utils";
import DatasetsPropertiesTable from "../../basic/datasets-properties/DatasetsPropertiesTable";
import { colorPallet } from "../charts/utils";
import DatasetInfoSearch from "../dataset-info-search/DatasetInfoSearch";
import ChartRenderer from "./ChartRenderer";
import ChartTypeMenu from "./ChartTypeMenu";
import { CurrentLineConfig } from "./DatasetLineConfigOptions";
import DateTimeRangeSelection from "./DateTimeRangeSelection";
import DragArea from "./DragArea/DragArea";
import MultiDragArea from "./DragArea/MultiDragArea";
import SettingsDialog from "./SettingsDialog";
import { IDataSet } from "./models/interfaces";
import { AggregationType, ChartType, Dataset } from "./models/types";
import { isDate2BeforeDate1 } from "./utils/utils";

const DatasetView = ({
  selectedDatasetsID,
  setSelectedDatasets,
  removeDataset,
  setCurrentLineConfig,
  initialChart,
  initialTimeOffset,
}: {
  selectedDatasetsID: Array<Dataset>;
  setSelectedDatasets: (arr: Array<Dataset>) => void;
  removeDataset: (datasetId: string) => void;
  setCurrentLineConfig: (identifier: string, currentLineConfig: CurrentLineConfig) => void;
  initialChart?: ChartType;
  initialTimeOffset?: ChronoUnitStr;
}) => {
  const [selectedChartType, setSelectedChartType] = useState<ChartType>(initialChart ?? "LINE");

  const [dateFrom, setDateFrom] = useState<Date>(addToDate(new Date(), -6, "HOURS"));
  const [dateTo, setDateTo] = useState<Date>(new Date());

  const [invalidRange, setInvalidRange] = useState<boolean>(false);

  const [dataPointCount, setDataPointCount] = useState<number>(0);

  const [displayProperties, setDisplayProperties] = useState<boolean>(false);
  const [displaySettings, setDisplaySettings] = useState<boolean>(false);

  const [enableSettings, setEnableSettings] = useState<boolean>(false);

  const [showAxisSelection, setShowAxisSelection] = useState<boolean>(false);
  const [enableMultiAxis, setEnableMultiAxkis] = useState<boolean>(false);
  const [selectedColorPalette, setSelectedColorPalette] = useState<{
    key: string;
    value: Array<string>;
  }>(colorPallet);

  const [datesRange, setDatesRange] = useState<Date[] | undefined>();
  const [selectedColor, setSelectedColor] = useState<ColorPickerValueType>("ff0000");

  const [xAxisID, setXAxisID] = useState<string>("");
  const [yAxisIDS, setYAxisIDS] = useState<Array<string>>([]);

  const [x1AxisID, setX1AxisID] = useState<string>("");
  const [y1AxisID, setY1AxisID] = useState<string>("");
  const [colorAxisID, setColorAxisID] = useState<string>("");
  const [sizeAxisID, setSizeAxisID] = useState<string>("");

  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);

  const [colorScale, setColorScale] = useState<boolean>(true);

  const [aggregationMethod, setAggregationMethod] = useState<AggregationType>("SUM");
  const [scatterSelectedTimeOffset, setScatterSelectedTimeOffset] = useState<string | undefined>(
    undefined
  );
  const [currentScatterData, setCurrentScatterData] = useState<
    (ScatterData | DatasetDataInfoResponse)[]
  >([]);

  const { data, isLoading, isError, refetch, isRefetching } = useGetDataInfoRange(
    selectedDatasetsID.map(e => e.identifier),
    {
      timeStart: dateFrom.toISOString(),
      timeEnd: dateTo.toISOString(),
      usePromiseHandler: res => {
        return res.data as (ScatterData | DatasetDataInfoResponse)[];
      },
    },
    {
      enabled: selectedDatasetsID.length > 0 && dateFrom.getTime() < dateTo.getTime(),
      refetchOnWindowFocus: false,
    }
  );
  const isDatasetDataInfoResponse = (
    data: ScatterData | DatasetDataInfoResponse
  ): data is DatasetDataInfoResponse => {
    return (data as DatasetDataInfoResponse).data !== undefined;
  };

  useEffect(() => {
    if (data != null) {
      setDataPointCount(
        data.reduce((acc: number, current: ScatterData | DatasetDataInfoResponse) => {
          if (isDatasetDataInfoResponse(current)) {
            return acc + current.data.length;
          }
          return acc + (current.x ? current.x.length : 0);
        }, 0)
      );

      if (
        currentScatterData.length > 2 &&
        scatterSelectedTimeOffset !== undefined &&
        selectedChartType === "MULTISCATTER"
      ) {
        const currentColorDataset = currentScatterData[2];
        const newCurrentData = [...data.slice(0, 2), currentColorDataset, ...data.slice(2)];
        setCurrentScatterData(newCurrentData);
      } else {
        setCurrentScatterData(data);
      }
    } else {
      setDataPointCount(0);
    }
  }, [data]);

  useEffect(() => {
    if (isDate2BeforeDate1(dateFrom, dateTo)) {
      setInvalidRange(true);
    } else {
      setDataPointCount(0);
      setInvalidRange(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (selectedDatasetsID.length === 0) {
      setEnableSettings(false);
    }
  }, [selectedDatasetsID]);

  useEffect(() => {
    if (selectedChartType.length > 0 && selectChartType != null && !isLoading) {
      setEnableSettings(true);
    }
  }, [selectedChartType]);

  const selectChartType = (chartType: ChartType) => {
    setSelectedChartType(chartType);
    if (chartType === "SCATTER") {
      setShowAxisSelection(true);
    } else {
      setShowAxisSelection(false);
    }

    if (chartType === "MULTISCATTER") {
      setEnableMultiAxkis(true);
    } else {
      setEnableMultiAxkis(false);
    }
  };

  const selectAggregationMethod = (currentAggregationMethod: AggregationType) => {
    setAggregationMethod(currentAggregationMethod);
  };

  const handleSettings = () => {
    setDisplaySettings(true);
  };

  const onHide = () => {
    setDisplaySettings(false);
  };

  const selectColorPalette = (colorsArr: { key: string; value: Array<any> }) => {
    setSelectedColorPalette(colorsArr);
  };

  const assignToAxis = useCallback((xAxisId: string, yAxisIds: Array<string>) => {
    setXAxisID(xAxisId);
    setYAxisIDS(yAxisIds);
  }, []);
  
  const assignToMultiAxis = (
    x1AxisId: string,
    y1AxisId: string,
    colorAxisId: string,
    sizeAxisId: string,
    scale: boolean
  ) => {
    setX1AxisID(x1AxisId);
    setY1AxisID(y1AxisId);
    setColorAxisID(colorAxisId);
    setSizeAxisID(sizeAxisId);
    setColorScale(scale);
  };

  const { width, ref: refOptions } = useResizeDetector();
  const { height, ref, width: widthWindow } = useResizeDetector();

  let dataToRender: (DatasetDataInfoResponse | ScatterData)[] = [];
  if (selectedDatasetsID.length > 0) {
    if (selectedChartType === "MULTISCATTER") {
      if (
        currentScatterData[2] &&
        isDatasetDataInfoResponse(currentScatterData[2]) &&
        currentScatterData[2].data.length !==
          (currentScatterData[0] as DatasetDataInfoResponse).data.length
      ) {
        currentScatterData[2].identifier ===
          (currentScatterData[0] as DatasetDataInfoResponse).identifier;
        currentScatterData[2].data = (currentScatterData[0] as DatasetDataInfoResponse).data || [];
        setColorAxisID(currentScatterData[2].identifier);
      }
      dataToRender = currentScatterData;
    } else {
      dataToRender = data || [];
    }
  }

  const handleUnselectColorDataset = () => {
    if (selectedDatasetsID[2]) {
      const currentSelectedDatasets = selectedDatasetsID;
      const colorDataset = selectedDatasetsID[2];
      const newSelectedDatasets = currentSelectedDatasets.filter(
        dataset => dataset.identifier !== colorDataset.identifier
      );
      setSelectedDatasets(newSelectedDatasets);
    }
  };

  return (
    <div className="datasetView dynamicRow fullWidth">
      {displaySettings && selectedChartType && (
        <SettingsDialog
          displayDialog={displaySettings}
          selectedChartType={selectedChartType}
          onHide={onHide}
          data={data || []}
          selectColorPalette={selectColorPalette}
          selectedColorPalette={selectedColorPalette}
          selectAggregationMethod={selectAggregationMethod}
          setDatesRange={setDatesRange}
          setSelectedColor={setSelectedColor}
          selectedColor={selectedColor}
        />
      )}

      {displayProperties && (
        <Dialog
          onHide={() => setDisplayProperties(false)}
          visible={displayProperties}
          header="Datasets Properties"
          style={{ width: "35vw" }}
          modal={false}
        >
          <DatasetsPropertiesTable identifiers={selectedDatasetsID.map(e => e.identifier)} />
        </Dialog>
      )}

      <div className="chartMenu cardSlim">
        <ChartTypeMenu
          selectedChartType={selectedChartType}
          setSelectedCharType={selectChartType}
          selectedDatasetID={selectedDatasetsID}
          setSelectedDatasets={setSelectedDatasets}
        />
      </div>

      <div className="flexColumn fullWidth">
        <div className="optionsFields cardSlim" ref={refOptions}>
          <div className="dateTimeSelection center">
            <DateTimeRangeSelection
              selectDateFrom={setDateFrom}
              selectDateTo={setDateTo}
              dateFrom={dateFrom}
              dateTo={dateTo}
              invalidRange={invalidRange}
              initialTimeOffset={initialTimeOffset}
            />
          </div>
          <div className="side flexRow center">
            {!isLoading && (
              <div className="flexColumn center">
                <h3>{selectedChartType === "RAW" ? "DATA TABLE" : selectedChartType}</h3>
                <p>Loaded data points: {dataPointCount}</p>
              </div>
            )}
            <Button
              className="p-button-secondary p-button-rounded p-button-text"
              icon="pi pi-search"
              onClick={() => setIsSearchOpen(true)}
            />
            <Button
              icon="pi pi-info-circle"
              onClick={() => setDisplayProperties(true)}
              className="p-button-rounded p-button-secondary p-button-text center"
              disabled={selectedDatasetsID.length === 0}
            />
            <Button
              icon="pi pi-cog"
              iconPos="left"
              onClick={handleSettings}
              className="p-button-rounded p-button-secondary p-button-text center"
              disabled={!enableSettings || !data}
            />
            <Button
              icon="pi pi-refresh"
              iconPos="left"
              onClick={() => refetch()}
              className="p-button-rounded p-button-secondary p-button-text center"
              disabled={isLoading || isError || isRefetching || !data}
              loading={isLoading || isRefetching}
            />
          </div>
        </div>

        <div className="dataView" style={{ width }} ref={ref}>
          <ChartRenderer
            selectedChartType={selectedChartType}
            data={dataToRender as IDataSet[]}
            isLoading={isLoading}
            isError={isError}
            colorPallet={selectedColorPalette}
            dataPointCount={dataPointCount}
            singleAxis={{ x: xAxisID, y: yAxisIDS }}
            multiAxis={{ x: x1AxisID, y: y1AxisID, color: colorAxisID, size: sizeAxisID }}
            colorScale={colorScale}
            selectedDatasetsID={selectedDatasetsID}
            aggregationType={aggregationMethod}
            windowWidth={widthWindow ?? 500}
            windowHeight={height && height < 500 ? height : 500}
            datesRange={datesRange}
            selectedColor={selectedColor}
            scatterSelectedTimeOffset={scatterSelectedTimeOffset}
          />
        </div>
        <div className="cardSlim">
          <div className="flex flex-row justify-content-between">
            <DragArea
              selectedDatasetsArr={selectedDatasetsID}
              removeDataset={removeDataset}
              showAxisSelection={showAxisSelection}
              assignToAxis={assignToAxis}
              selectedChartType={selectedChartType}
              setCurrentLineConfig={setCurrentLineConfig}
            />
          </div>
          {enableMultiAxis && (
            <MultiDragArea
              selectedDatasetsArr={selectedDatasetsID}
              removeDataset={removeDataset}
              enableXAxis
              assignToMultiAxis={assignToMultiAxis}
              colorScale={colorScale}
              scatterSelectedTimeOffset={scatterSelectedTimeOffset}
              currentScatterData={currentScatterData}
              setScatterSelectedTimeOffset={setScatterSelectedTimeOffset}
              setColorScale={setColorScale}
              setCurrentScatterData={setCurrentScatterData}
              data={data}
              handleUnselectColorDataset={handleUnselectColorDataset}
              setColorAxisID={setColorAxisID}
            />
          )}
        </div>
      </div>
      {isSearchOpen && (
        <DatasetInfoSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      )}
    </div>
  );
};

export default DatasetView;
